// KEY-460: an action point can declare fields it cannot be worked without. Until they carry values its
// items are out of reach — flagged in the section's list, and locked on the action point itself.
//
// The rule is enforced on the SERVER (a 409 from the item-status write path); everything here is about
// saying so before someone runs into it, which is why the copy names the fields rather than just refusing.

import type { SegmentRequirement } from "@keydials/api-client";
import { DEFAULT_SEGMENT_NOUN, type SegmentNoun } from "../vocabulary";

/** Whether this action point's items are out of reach. */
export function segmentIsLocked(missing: readonly SegmentRequirement[]): boolean {
  return missing.length > 0;
}

/** The missing fields as a readable list: "Site lead", "Site lead and Risk owner", "A, B and C". */
export function requirementNames(missing: readonly SegmentRequirement[]): string {
  const labels = missing.map((m) => m.label);
  if (labels.length <= 1) {
    return labels[0] ?? "";
  }
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/** The hover/aria hint on a locked Items tab or a flagged action point; undefined when nothing is missing. */
export function requirementHint(missing: readonly SegmentRequirement[]): string | undefined {
  if (missing.length === 0) {
    return undefined;
  }
  const noun = missing.length === 1 ? "field" : "fields";
  return `Needs ${missing.length} ${noun} filled in first: ${requirementNames(missing)}`;
}

/**
 * What the lock says. Two versions, because a dead end is worse than a refusal: someone who can fill the
 * fields in is told where to go, and someone who can't is told plainly that it isn't theirs to do rather
 * than being sent to a tab that will refuse them too.
 */
export function requirementMessage(
  missing: readonly SegmentRequirement[],
  canEdit: boolean,
  // KEY-539: the dial's own word for an action point. Defaulted so every existing caller and test keeps
  // today's wording without being touched.
  noun: SegmentNoun = DEFAULT_SEGMENT_NOUN,
): string {
  const names = requirementNames(missing);
  return canEdit
    ? `This ${noun.one} needs ${names} filled in before its items can be worked on. Open the Key Info tab to add ${missing.length === 1 ? "it" : "them"}.`
    : `This ${noun.one} needs ${names} filled in before its items can be worked on. You can't edit ${missing.length === 1 ? "that field" : "those fields"} — ask someone who looks after this dial.`;
}
